import dayjs from 'dayjs'
import { TrackedChangeType, TrackedChange } from 'prose-core'
import { Fragment, Node, type Node as ProseNode } from 'prosemirror-model'
import { EditorState, Transaction } from 'prosemirror-state'
import { Mapping } from 'prosemirror-transform'

import { breakOutOfList as command } from 'common/Prose/Commands/lists/breakOutOfList'
import { ourUuid } from 'utils'

export const wrappedCommand = (state: EditorState, tr: Transaction, map: Mapping) => {
  command(state, (dispatchedTr) => {
    dispatchedTr.steps.forEach((step) => {
      tr.step(step)
    })
    map.appendMapping(tr.mapping)
  })
}

export function getTrackedChangesFromNode(node: Node<any>, trackedChangeType: TrackedChangeType, userId: number) {
  return (node.attrs.trackedChanges || [])
    .filter(({ type }: TrackedChange) => ![TrackedChangeType.Insertion, TrackedChangeType.Deletion].includes(type))
    .concat({ type: trackedChangeType, userId, createdAt: dayjs().toISOString(), uuid: ourUuid() })
}

export function createTrackedChange(trackedChangeType: TrackedChangeType, userId: number, uuid: string = ourUuid()) {
  return { type: trackedChangeType, userId, createdAt: dayjs().toISOString(), uuid }
}

interface WorkableNode {
  node: ProseNode
  from: number
  to: number
}

export interface MatchingNode extends WorkableNode {
  parentList: WorkableNode[]
}

type NodeSearchResponse =
  | { status: 'found'; match: MatchingNode; stack: MatchingNode[] }
  | { status: 'canceled'; match: null; stack: MatchingNode[] }
  | { status: 'notFound'; match: null; stack: MatchingNode[] }

/**
 * Efficient heuristic search that returns stack of parent nodes and from / to positions.
 * This is meant to be used with the doc, if you start from a node please remember to offset the positions.
 *
 * Heuristic function:
 * - 'found': return the node
 * - 'skip': skip this node and continue
 * - 'search': search this node's children
 * - 'stop': stop searching (results in status canceled)
 *
 * All positions are relative to the document (global)
 *
 * @param frag
 * @param heuristic
 * @param parent
 * @returns {NodeSearchResponse}
 * */
export function fastHeuristicSearch(
  fragment: Fragment,
  heuristic: (info: MatchingNode) => 'found' | 'stack' | 'skip' | 'search' | 'stop',
  parent?: MatchingNode,
  stack: MatchingNode[] = [],
): NodeSearchResponse {
  const start = parent ? parent.from + 1 : 0
  for (let i = 0, position = 0; i < fragment.size; i++) {
    const from = start + position
    const child = fragment.content[i]
    if (!child) break
    const end = position + child.nodeSize

    const currentNode: MatchingNode = {
      node: child,
      from,
      to: from + child.nodeSize,
      parentList: parent ? [...parent.parentList, parent] : [],
    }

    const decision = heuristic(currentNode)
    switch (decision) {
      case 'found':
        return { status: 'found', match: currentNode, stack }
      case 'stop':
        return { status: 'canceled', match: null, stack }
      case 'skip':
        break
      case 'stack':
        stack.push(currentNode) // eslint-disable-line no-fallthrough
      case 'search':
        if (child.content != null) {
          const nextDecision = fastHeuristicSearch(child.content, heuristic, currentNode, stack)
          if (nextDecision.status === 'found' || nextDecision.status === 'canceled') {
            return nextDecision
          }
        }
        break
    }
    position = end
  }
  return { status: 'notFound', match: null, stack }
}
